import { Injectable } from '@angular/core';
import { UserSubscription } from '@ca/core/models';
import { AppEnvironmentConfig } from '@ca/environment/app-environment-config.model';
import mixpanel from 'mixpanel-browser';
import { environment } from '../../../../environments/environment';
import { FileModel } from '../../models/file-model.model';

interface TrackingLibrary {
    init(token: string): void;
    track(event: string, properties?: Object): void;
    identify(userId: string, userName: string, email: string | undefined, environment: AppEnvironmentConfig, subscription: UserSubscription): void;
    setUserProperties(properties: Object): void;
    reset(): void;
}

@Injectable({
    providedIn: 'root',
})
export class MixpanelLibrary implements TrackingLibrary {
    init(token: string): void {
        if (environment.name === 'Production') {
            mixpanel.init(token);
        }
    }

    track(event: string, properties?: Object): void {
        if (environment.name === 'Production') {
            mixpanel.track(event, properties);
        }
    }

    identify(userId: string, userName: string, email: string, environment: AppEnvironmentConfig, subscription: UserSubscription): void {
        if (environment.name === 'Production') {
            // Reset to ensure no previous user associations exist
            mixpanel.reset();

            // Set distinct_id to userId and register other properties
            mixpanel.register({
                distinct_id: userId,
                name: userName,
                environment: environment.name,
            });

            if (userId && subscription) {
                const userData = {
                    $distinct_id: userId,
                    $name: userName || 'Unknown name',
                    $email: email || 'Unknown email',
                    'Subscription Type': subscription?.name || 'Unknown subscription type',
                    'Subscription Key': subscription?.subscriptionKey || 'Unknown',
                    'Subscription StartDate': subscription?.startDate || 'Unknown',
                    'Subscription EndDate': subscription?.endDate || 'Unknown',
                };

                // Set user properties, including subscription and user data
                mixpanel.people.set(userData);
            }

            // Identify the user by userId
            mixpanel.identify(userId);
        }
    }

    setUserProperties(properties: Object): void {
        if (environment.name === 'Production') {
            mixpanel.people.set(properties);
        }
    }

    reset(): void {
        if (environment.name === 'Production') {
            mixpanel.reset();
        }
    }
}

@Injectable({
    providedIn: 'root',
})
export class TrackingService {
    private trackingLibrary: TrackingLibrary;

    constructor(private mixpanelLibrary: MixpanelLibrary) {
        this.trackingLibrary = this.mixpanelLibrary;
        this.init();
    }

    private init(): void {
        const { mixpanelToken: token, name, MED } = environment;

        if (!token) {
            console.error('Mixpanel tracking token not found!');
            return;
        }
        if (name !== 'Production') {
            return;
        }
        if (MED) {
            return;
        }
        this.trackingLibrary.init(token);
    }

    trackEvent(eventName: string, properties: { [key: string]: number | string | string[] | undefined | FileModel[] }): void {
        if (environment.name != 'Production') {
            if (environment.MED) {
                return;
            } else {
                return;
            }
        }

        properties.environment = environment.name;
        this.trackingLibrary.track(eventName, properties);
    }

    identifyUser(userId: string, userName: string, email: string | undefined, environment: AppEnvironmentConfig, subscription: UserSubscription): void {
        this.trackingLibrary.identify(userId, userName, email, environment, subscription);
    }

    setUserProperties(properties: Object): void {
        this.trackingLibrary.setUserProperties(properties);
    }

    resetTracking(): void {
        this.trackingLibrary.reset();
    }
}
